import { useEffect, useMemo, useState } from "react";
import { useMap } from "react-leaflet";
import {
  fetchMitecoRegion,
  fetchMitecoManifest,
  regionIntersectsBounds,
  withLoadedRegionIds,
} from "../model/mitecoLayers.ts";
import type { MapMitecoMode } from "../model/mitecoLayers.ts";
import { MITECO_SUBBASIN_STYLE } from "./mapStyles.ts";
import { MitecoSelectableLayer } from "./MitecoSelectableLayer.tsx";

const ATTRIBUTION = '&copy; <a href="https://www.miteco.gob.es">MITECO</a>';

/**
 * MITECO's official sub-basins — a static, pre-processed copy split by
 * hydrographic demarcation (see mitecoLayers.ts for why). Only fetches the
 * region(s) that actually intersect the current map view, and re-checks on
 * every pan/zoom, so a small project never pulls in the whole country.
 * Only mounted while SubBasinsPanel.tsx's "Add from MITECO" mode is active
 * AND set to this layer — see SubBasinMap.tsx.
 */
export function MitecoSubBasinsLayer({ mode }: { mode: MapMitecoMode }) {
  const map = useMap();
  const [loadedIds, setLoadedIds] = useState<string[]>([]);
  const [regions, setRegions] = useState(new Map<string, GeoJSON.FeatureCollection>());

  useEffect(() => {
    let cancelled = false;

    async function syncToView() {
      const manifest = await fetchMitecoManifest();
      const b = map.getBounds();
      const viewBounds: [number, number, number, number] = [
        b.getWest(),
        b.getSouth(),
        b.getEast(),
        b.getNorth(),
      ];
      const needed = manifest.filter(
        (region) => regionIntersectsBounds(region, viewBounds) && !loadedIds.includes(region.id)
      );
      if (needed.length === 0) return;
      const fetched = await Promise.all(needed.map(fetchMitecoRegion));
      if (cancelled) return;
      setRegions((prev) => {
        const next = new Map(prev);
        needed.forEach((region, i) => next.set(region.id, fetched[i]));
        return next;
      });
      // withLoadedRegionIds dedupes against the LATEST `ids`, not just
      // `loadedIds`'s stale closure — see its own comment for why a plain
      // append isn't safe here.
      setLoadedIds((ids) =>
        withLoadedRegionIds(
          ids,
          needed.map((r) => r.id)
        )
      );
    }

    syncToView();
    map.on("moveend", syncToView);
    return () => {
      cancelled = true;
      map.off("moveend", syncToView);
    };
  }, [map, loadedIds]);

  const data = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: "FeatureCollection",
      features: loadedIds.flatMap((id) => regions.get(id)?.features ?? []),
    }),
    [loadedIds, regions]
  );

  return (
    <MitecoSelectableLayer
      data={data}
      style={MITECO_SUBBASIN_STYLE}
      attribution={ATTRIBUTION}
      mode={mode}
    />
  );
}
